<template>
  <div v-if="isRolePermitted" class="distribution-account-management">
    <el-card shadow="never" class="animate__animated animate__fadeInUp">
      <div class="filter-card">
        <div class="filter-toolbar">
          <div class="filter-search">
            <el-input
              v-model="searchUsername"
              placeholder="搜索用户名"
              prefix-icon="el-icon-search"
              clearable
              @keyup.enter.native="handleSearch"
            ></el-input>
            <el-button
              type="primary"
              icon="el-icon-search"
              @click="handleSearch"
            >搜索</el-button>
          </div>
          <div class="filter-actions">
            <el-button
              type="success"
              icon="el-icon-plus"
              @click="resetAndShowAddForm"
            >添加管理员账户</el-button>
          </div>
        </div>
      </div>

      <div v-if="error" class="error">{{ error }}</div>
      <el-empty
        v-else-if="!loading && allAccounts.length === 0"
        description="暂无管理员账户数据"
      />

      <div v-else>
        <div class="table-scroll-wrapper">
        <el-table
          :data="filteredAccounts"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载账户数据..."
          class="animate__animated animate__fadeIn"
        >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="username" label="用户名" min-width="150" show-overflow-tooltip />
          <el-table-column label="账户状态" width="120" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
                {{ scope.row.status === 1 ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" align="center">
            <template slot-scope="scope">
              <el-button
                size="mini"
                :type="scope.row.status === 1 ? 'warning' : 'success'"
                icon="el-icon-lock"
                @click="handleChangeStatus(scope.row)"
              >{{ scope.row.status === 1 ? '禁用' : '启用' }}</el-button>
              <el-button
                size="mini"
                type="danger"
                icon="el-icon-delete"
                @click="handleDeleteAccount(scope.row)"
              >删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        </div>

        <div class="pagination-container">
          <el-pagination
            background
            :layout="paginationLayout"
            :current-page="pagination.pageNum"
            :page-size="pagination.pageSize"
            :page-sizes="[5, 10, 20, 50]"
            :total="totalAccounts"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 添加管理员账户对话框 -->
    <el-dialog
      title="添加管理员账户"
      :visible.sync="showAddForm"
      :width="isMobile ? '92%' : '500px'"
      center
    >
      <el-form :model="currentAccount" :rules="accountRules" ref="accountForm" :label-width="isMobile ? '80px' : '120px'">
        <el-form-item label="用户名" prop="username">
          <el-input
            v-model="currentAccount.username"
            placeholder="请输入用户名"
          ></el-input>
        </el-form-item>

        <el-form-item label="密码" prop="password">
          <el-input
            v-model="currentAccount.password"
            type="password"
            placeholder="请输入密码"
          ></el-input>
        </el-form-item>

        <el-form-item label="账户状态" prop="status">
          <el-radio-group v-model="currentAccount.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="showAddForm = false">取 消</el-button>
        <el-button type="primary" @click="submitAccountForm">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 删除确认对话框 -->
    <el-dialog
      title="删除管理员账户"
      :visible.sync="deleteDialogVisible"
      :width="isMobile ? '92%' : '400px'"
      center
    >
      <p>警告：此操作将永久删除管理员账户 <strong>{{ currentAccount.username }}</strong>！</p>
      <p>该操作不可撤销，所有相关数据也将被删除。</p>
      <p>请确认是否继续执行此操作？</p>
      <div slot="footer" class="dialog-footer">
        <el-button @click="deleteDialogVisible = false">取 消</el-button>
        <el-button type="danger" @click="confirmDeleteAccount">确 定删除</el-button>
      </div>
    </el-dialog>
  </div>
  <div v-else class="no-permission">
    <el-result icon="warning" title="权限不足" sub-title="您的账户权限不足，无法查看此页面">
    </el-result>
  </div>
</template>

<script>
import { request } from '@/utils/http'
import responsiveMixin from '@/mixins/responsive'

export default {
  name: 'DistributionAccountManagement',
  mixins: [responsiveMixin],
  data() {
    return {
      allAccounts: [], // 存储所有账户数据
      loading: false,
      error: '',
      searchUsername: '',
      showAddForm: false,
      deleteDialogVisible: false,
      currentAccount: {},
      pagination: {
        pageNum: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    // 根据搜索条件过滤账户
    isRolePermitted() {
      // 获取本地存储的角色，默认为0（即允许访问）
      const role = localStorage.getItem('role');
      return role === '0';
    },
    
    filteredAccounts() {
      if (!this.searchUsername) {
        // 如果没有搜索条件，返回当前页的数据
        const startIndex = (this.pagination.pageNum - 1) * this.pagination.pageSize;
        const endIndex = startIndex + this.pagination.pageSize;
        return this.allAccounts.slice(startIndex, endIndex);
      }
      
      // 如果有搜索条件，返回过滤后的数据并分页
      const filtered = this.allAccounts.filter(account => 
        account.username.toLowerCase().includes(this.searchUsername.toLowerCase())
      );
      
      const startIndex = (this.pagination.pageNum - 1) * this.pagination.pageSize;
      const endIndex = startIndex + this.pagination.pageSize;
      return filtered.slice(startIndex, endIndex);
    },
    
    // 总账户数（用于分页）
    totalAccounts() {
      if (!this.searchUsername) {
        return this.allAccounts.length;
      }
      
      return this.allAccounts.filter(account => 
        account.username.toLowerCase().includes(this.searchUsername.toLowerCase())
      ).length;
    },
    
    // 表单验证规则
    accountRules() {
      return {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度在3到20个字符', trigger: 'blur' }
        ],
        password: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (!value) {
                callback(new Error('请输入密码'));
              } else if (value.length < 6 || value.length > 20) {
                callback(new Error('密码长度在6到20个字符'));
              } else {
                callback();
              }
            },
            trigger: 'blur'
          }
        ],
        status: [
          { required: true, message: '请选择账户状态', trigger: 'change' }
        ]
      };
    }
  },
  async mounted() {
    await this.fetchAccounts()
  },
  methods: {
    async fetchAccounts() {
      this.loading = true
      this.error = ''
      
      try {
        // 获取所有管理员账号
        const response = await request('/api/AdminAccount/all', {
          method: 'GET'
        });
        
        if (response.code === 200) {
          this.allAccounts = response.data;
        } else {
          throw new Error(response.msg || '获取账户列表失败');
        }
      } catch (error) {
        console.error('获取管理员账户列表失败:', error)
        this.error = error.message || '获取管理员账户列表失败'
      } finally {
        this.loading = false
      }
    },
    
    handleCurrentChange(pageNum) {
      this.pagination.pageNum = pageNum;
    },
    
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.pageNum = 1;
    },
    
    handleSearch() {
      this.pagination.pageNum = 1; // 搜索时回到第一页
    },
    
    handleChangeStatus(account) {
      const newStatus = account.status === 1 ? 0 : 1;
      this.$confirm(
        `您确定要${newStatus === 1 ? '启用' : '禁用'}账户 "${account.username}" 吗？`, 
        '提示', 
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(async () => {
        try {
          // 使用统一的更新接口更新状态
          const response = await request('/api/AdminAccount/distribution-ratio-and-status', {
            method: 'PUT',
            body: JSON.stringify({
              username: account.username,
              status: newStatus
            })
          });
          
          if (response.code === 200) {
            this.$message.success(response.data || '状态更新成功');
            account.status = newStatus;
          } else {
            throw new Error(response.msg || '状态更新失败');
          }
        } catch (error) {
          this.$message.error(error.message || `${newStatus === 1 ? '启用' : '禁用'}失败`);
        }
      }).catch(() => {
        // 用户取消操作
      });
    },
    
    handleDeleteAccount(account) {
      this.currentAccount = account;
      this.deleteDialogVisible = true;
    },
    
    async confirmDeleteAccount() {
      try {
        // 使用新的删除接口，通过路径参数传递用户名
        const response = await request(`/api/AdminAccount/${this.currentAccount.username}`, {
          method: 'DELETE'
        });
        
        if (response.code === 200) {
          this.$message.success(response.data || '管理员账号删除成功');
          this.deleteDialogVisible = false;
          this.fetchAccounts(); // 刷新列表
        } else {
          throw new Error(response.msg || '删除账户失败');
        }
      } catch (error) {
        this.$message.error(error.message || '删除账户失败');
      }
    },
    
    resetAndShowAddForm() {
      this.currentAccount = {
        username: '',
        password: '',
        status: 1
      };
      this.showAddForm = true;
    },
    
    submitAccountForm() {
      this.$refs.accountForm.validate(async (valid) => {
        if (!valid) {
          return false;
        }

        try {
          const response = await request('/api/AdminAccount/add', {
            method: 'POST',
            body: JSON.stringify({
              username: this.currentAccount.username,
              password: this.currentAccount.password,
              status: parseInt(this.currentAccount.status)
            }),
          });

          if (response.code === 200) {
            this.$message.success(response.data || '管理员账号添加成功');
            this.showAddForm = false;
            this.fetchAccounts();
          } else {
            throw new Error(response.msg || '添加账户失败');
          }
        } catch (error) {
          this.$message.error(error.message || '添加账户失败');
        }
      });
    },
    
  }
}
</script>

<style scoped>
.distribution-account-management {
  padding: 0;
}

.no-permission {
  padding: 40px;
}

.filter-card {
  margin-bottom: 20px;
}

.error {
  color: #f56c6c;
  padding: 10px;
  background-color: #fef0f0;
  border-radius: 4px;
  margin-bottom: 20px;
}

.el-table {
  margin-bottom: 20px;
}

.pagination-container {
  display: flex;
  justify-content: center;
}

@media (max-width: 768px) {
  .el-table .el-button + .el-button {
    margin-left: 0;
    margin-top: 4px;
  }
}

.dialog-footer {
  text-align: right;
}
</style>